// Utility functions for handling large datasets in Excel downloads

export const MemoryUtils = {
  // Check available memory (approximate)
  checkMemoryUsage: () => {
    if ('memory' in performance) {
      return {
        used: Math.round(performance.memory.usedJSHeapSize / 1048576), // MB
        total: Math.round(performance.memory.totalJSHeapSize / 1048576), // MB
        limit: Math.round(performance.memory.jsHeapSizeLimit / 1048576) // MB
      }
    }
    return null
  },

  // Force garbage collection (if available)
  forceGarbageCollection: () => {
    if (window.gc) {
      window.gc()
    }
  },

  // Calculate optimal chunk size based on data
  calculateOptimalChunkSize: (dataLength, estimatedRowSize = 1000) => {
    const memInfo = MemoryUtils.checkMemoryUsage()
    
    if (!memInfo) {
      // Fallback values for browsers without memory API
      if (dataLength > 100000) return 2000
      if (dataLength > 50000) return 3000
      return 5000
    }

    const availableMemory = (memInfo.limit - memInfo.used) * 0.3 // Use 30% of available memory
    const estimatedChunkMemory = estimatedRowSize * 100 / 1048576 // 100 rows in MB
    
    const optimalChunkSize = Math.floor(availableMemory / estimatedChunkMemory) * 100
    
    // Ensure chunk size is within reasonable bounds
    return Math.max(1000, Math.min(10000, optimalChunkSize))
  },

  // Monitor memory usage during processing
  memoryMonitor: {
    start: () => {
      if ('memory' in performance) {
        return performance.memory.usedJSHeapSize
      }
      return Date.now()
    },

    check: (startValue, threshold = 100 * 1048576) => { // 100MB threshold
      if ('memory' in performance) {
        const currentUsage = performance.memory.usedJSHeapSize
        return (currentUsage - startValue) > threshold
      }
      return false
    }
  }
}

export const ExcelOptimizer = {
  // Optimize data before processing
  optimizeDataForExcel: (data, headers) => {
    return data.map(row => {
      const optimizedRow = {}
      
      headers.forEach(header => {
        let value = row[header.columnName]
        
        // Handle different data types for Excel optimization
        if (value === null || value === undefined) {
          optimizedRow[header.columnName] = ''
        } else if (typeof value === 'string') {
          // Truncate very long strings to prevent memory issues
          optimizedRow[header.columnName] = value.length > 32767 
            ? value.substring(0, 32767) + '...' 
            : value
        } else if (typeof value === 'object') {
          // Convert objects to strings
          optimizedRow[header.columnName] = JSON.stringify(value)
        } else {
          optimizedRow[header.columnName] = value
        }
      })
      
      return optimizedRow
    })
  },

  // Clean up memory after processing chunks
  cleanupAfterChunk: () => {
    // Force garbage collection if available
    MemoryUtils.forceGarbageCollection()
    
    // Clear any temporary variables
    if (global.gc) {
      global.gc()
    }
  },

  // Validate data size before processing
  validateDataSize: (dataLength) => {
    const warnings = []
    
    if (dataLength > 500000) {
      warnings.push('Dataset exceeds 500k records. Consider filtering data before export.')
    }
    
    if (dataLength > 200000) {
      warnings.push('Large dataset detected. Download may take several minutes.')
    }

    const memInfo = MemoryUtils.checkMemoryUsage()
    if (memInfo && (memInfo.used / memInfo.limit) > 0.7) {
      warnings.push('High memory usage detected. Consider closing other applications.')
    }

    return warnings
  }
}

export const PerformanceTracker = {
  timers: new Map(),

  start: (label) => {
    PerformanceTracker.timers.set(label, performance.now())
  },

  end: (label) => {
    const startTime = PerformanceTracker.timers.get(label)
    if (startTime) {
      const duration = performance.now() - startTime
      PerformanceTracker.timers.delete(label)
      return Math.round(duration)
    }
    return 0
  },

  log: (label, message) => {
    const duration = PerformanceTracker.end(label)
    console.log(`[Excel Export] ${message} - ${duration}ms`)
    return duration
  }
}